import { useCallback } from 'react';
import { useAuth } from '../features/auth/contexts/AuthContext';

export interface DocumentDTO {
  _id: string;
  originalFileName: string;
  fileName: string;
  mimeType: string;
  size: number;
  path: string;
  hash: string;
  tags: string[];
  status: string;
  step?: string;
  documentType?: string;
  isValid?: boolean;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const BASE_URL = import.meta.env.VITE_BFF_URL || 'http://localhost:3010';

export interface DocumentListResponse {
  data: DocumentDTO[];
  meta: {
    /* define meta properties if needed */
  };
}

export function useDocumentsApi() {
  const { logout } = useAuth();

  const handleUnauthorized = useCallback(async () => {
    try {
      await logout();
    } finally {
      window.location.replace('/login');
    }
  }, [logout]);

  /* GET /documents */
  const list = useCallback(async (): Promise<DocumentListResponse> => {
    const res = await fetch(`${BASE_URL}/documents`, {
      credentials: 'include',
    });
    if (res.status === 401 || res.status === 403) {
      await handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) throw new Error('Failed to fetch documents');
    return res.json();
  }, []);

  /* GET /documents/:id */
  const get = useCallback(async (id: string): Promise<DocumentDTO> => {
    const res = await fetch(`${BASE_URL}/documents/${id}`, {
      credentials: 'include',
    });
    if (res.status === 401 || res.status === 403) {
      await handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) throw new Error('Failed to fetch document');
    return res.json();
  }, []);

  /* GET /documents/:id/url */
  const presignedUrl = useCallback(async (id: string): Promise<string> => {
    const res = await fetch(`${BASE_URL}/documents/${id}/url`, {
      credentials: 'include',
    });
    if (res.status === 401 || res.status === 403) {
      await handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) throw new Error('Failed to fetch document URL');
    const json = await res.json();
    return json.url as string;
  }, []);

  /* POST /documents/:id/archive */
  const archive = useCallback(async (id: string): Promise<void> => {
    const res = await fetch(`${BASE_URL}/documents/${id}/archive`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.status === 401 || res.status === 403) {
      await handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) throw new Error('Failed to archive document');
  }, []);

  /* GET /documents/:id/download */
  const download = useCallback(async (id: string, filename: string): Promise<void> => {
    const res = await fetch(`${BASE_URL}/documents/${id}/download`, {
      credentials: 'include',
    });
    if (res.status === 401 || res.status === 403) {
      await handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) throw new Error('Failed to download document');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  /* POST /documents */
  const upload = useCallback(async (file: File, tags?: string[]): Promise<DocumentDTO> => {
    const formData = new FormData();
    formData.append('file', file);
    if (tags) {
      tags.forEach((tag) => formData.append('tags[]', tag));
    }

    const res = await fetch(`${BASE_URL}/documents`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (res.status === 401 || res.status === 403) {
      await handleUnauthorized();
      throw new Error('Unauthorized');
    }

    if (!res.ok) {
      const errorBody = await res.text();
      throw new Error(`Failed to upload document: ${res.statusText} - ${errorBody}`);
    }

    return res.json();
  }, []);

  /* POST /documents/:id/update */
  const update = useCallback(
    async (
      id: string,
      data: {
        documentType?: string;
        tags?: string[];
        metadata?: Record<string, any>;
      },
    ): Promise<DocumentDTO> => {
      const res = await fetch(`${BASE_URL}/documents/${id}/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        throw new Error('Unauthorized');
      }

      if (!res.ok) {
        throw new Error('Failed to update document');
      }

      return res.json();
    },
    [],
  );

  /* POST /documents/:id/process */
  const process = useCallback(async (id: string, type: string): Promise<DocumentDTO> => {
    const res = await fetch(`${BASE_URL}/documents/${id}/process`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id, type }),
    });
    if (res.status === 401 || res.status === 403) {
      await handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`Failed to process document: ${res.status} ${txt}`);
    }
    return res.json();
  }, []);

  return {
    list,
    get,
    presignedUrl,
    archive,
    download,
    upload,
    update,
    process,
  };
}
